import React, { useMemo } from 'react';

import AutoBoxIcon from 'components/AutoBoxIcon';
import AutoDetailPlace from 'components/AutoDetailPlace';
import { bgImage, bgLogo } from 'components/AutoDetails/util';
import AutoHowTo from 'components/AutoHowTo';
import AutoInfo from 'components/AutoInfo';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { VehicleOption, VehicleOptionDetailValidate } from 'models/auto/vehicle';
import { htmlT } from 'utils/generic';

import { Section, Header, IconKm } from './../../styles';

interface Props {
  data: VehicleOption | VehicleOptionDetailValidate;
  hasPickUpOptions: boolean;
  hasDropOffOptions: boolean;
  handleAgencies?: (isPickUp: boolean) => void;
  isDetails?: boolean;
  children?: JSX.Element;
  unlimitedKm?: boolean;
}
const AutoCardDetails = ({ data, hasPickUpOptions, hasDropOffOptions, handleAgencies, isDetails, children, unlimitedKm = false }: Props) => {
  const { t } = useStoreTranslation();
  const _bgImage = useMemo(() => (data.vehicle?.image ? bgImage(data.vehicle.image) : {}), [data]);
  const _bgLogo = useMemo(() => (data.company?.code ? bgLogo(data.company?.code) : {}), [data]);

  return (
    <>
      <Header $details={isDetails ? true : false}>
        <Header.Image style={_bgImage}>
          <Header.Logo style={_bgLogo} />
        </Header.Image>
      </Header>
      <Section>
        <Header.Title>{data.vehicle?.model}</Header.Title>
        <Header.Subtitle>{data.vehicle?.vehicleCategory}</Header.Subtitle>
      </Section>
      {data.vehicle &&
        data.vehicle.maxPax &&
        data.vehicle?.normalLuggage &&
        data.vehicle?.doors &&
        data.vehicle?.transmissionType && (
          <Section>
            <Header.Subtitle>{t('autosDetails.characteristics')}</Header.Subtitle>

            <AutoInfo
              data={{
                passengers: `${data.vehicle?.maxPax}`,
                luggage: `${data.vehicle?.normalLuggage}`,
                doors: `${data.vehicle?.doors}`,
                airConditioned: data.vehicle?.airConditioned || false,
                transmission: `${data.vehicle?.transmissionType}`,
                unlimitedMileage: unlimitedKm,
              }}
              full={false}
              showAirConditioned={true}
            />
          </Section>
        )}
      {data.pickUp && (
        <Section>
          <AutoDetailPlace data={data.pickUp} hasAgencies={hasPickUpOptions} onClick={handleAgencies} />
        </Section>
      )}
      {data.dropOff && (
        <Section>
          <AutoDetailPlace data={data.dropOff} isPickUp={false} hasAgencies={hasDropOffOptions} onClick={handleAgencies} />
        </Section>
      )}
      <Section.Sub>
        <AutoBoxIcon />
      </Section.Sub>
      {unlimitedKm && (
        <Section.Sub>
          <AutoBoxIcon text={htmlT(t, 'autosDetails.unlimitedKm')} icon={<IconKm />} />
        </Section.Sub>
      )}
      <Section.Sub>
        <AutoHowTo title={t('autosDetails.howto.title')}>{htmlT(t, 'autosDetails.howto.text')}</AutoHowTo>
      </Section.Sub>
      {children && (
        <Section.Sub>
          <AutoHowTo title={t('autosDetails.howto.comments')}>{children}</AutoHowTo>
        </Section.Sub>
      )}
    </>
  );
};

export default AutoCardDetails;
